import React, {
  useEffect,
  useState,
  useRef,
  SyntheticEvent,
  KeyboardEvent,
  useMemo,
  ReactNode
} from "react";
import { PolyIcon } from "elements";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { debounce } from "lodash";

export type OptionType = {
  value: string;
  text: string;
  count?: number;
  icon?: ReactNode;
  iconPosition?: "before" | "after";
  disabled?: boolean;
};

export interface OptionListProps {
  name: string;
  options: Array<OptionType>;
  enableTooltip?: boolean;
  visible: boolean;
  onClick: (value: OptionType) => void;
  onChange: (value: OptionType["value"]) => void;
  value?: OptionType["value"];
  noResultsKey: LocalKeys;
  focusIndex: number;
  inline: boolean;
  disabled?: boolean;
  testid?: string;
  dividerIndices?: Array<number>;
}

export const OptionList: React.FC<OptionListProps> = ({
  name,
  options,
  enableTooltip = false,
  visible,
  onClick,
  noResultsKey,
  onChange,
  value,
  focusIndex,
  inline,
  disabled,
  testid,
  dividerIndices
}) => {
  // React does not like null as the value for a select
  value = value ?? undefined;
  const { t } = useTranslation();

  useEffect(() => {
    const option = document.getElementById(`option-${focusIndex}`);
    if (option) {
      option.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
    }
  }, [focusIndex]);

  const testString = (index: number) => {
    return testid ? `${testid}-option-text-${index}` : "option-text";
  };

  return (
    <>
      <select
        id={name + "-select"}
        data-testid={name + "-select"}
        name={name}
        onChange={e => onChange(e.target.value)}
        value={value}
        className="hidden-select-state"
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        role="listbox"
        disabled={disabled}
      >
        {options.map((option, index) => (
          <option
            key={JSON.stringify(option.value) + index}
            value={option.value}
            onClick={() => onClick(option)}
            disabled={disabled || option.disabled}
          >
            {option.text}
          </option>
        ))}
      </select>
      {/*
        Since I apply the box-shadow to the results set and input div individually, the
        border between the two would show a shadow, so this is a hacky workaround that covers
        it using a div that sits in between (open to better solutions of course)
      */}
      <div className={`shadow-blocker ${visible ? "visible" : ""}`} />
      <ul
        className={`options-list ${visible ? "open" : ""} ${
          inline ? "inline" : ""
        }`}
      >
        {options.map((option, index) => {
          const classes = [
            "option",
            index === focusIndex ? "focus" : "",
            option.value === value ? "active" : "",
            option.disabled ? "disabled" : ""
          ]
            .filter(c => !!c)
            .join(" ");

          const { icon, iconPosition = "before" } = option;
          const iconContainerClasses = [
            "d-flex align-center",
            iconPosition === "after" ? "flex-row-reverse space-between" : ""
          ]
            .filter(c => !!c)
            .join(" ");
          const iconClasses = iconPosition === "before" ? "mr-5" : "ml-15";
          return (
            <React.Fragment key={option.value + index}>
              <li
                className={classes}
                onClick={() => onClick(option)}
                key={option.value + index}
                id={`option-${index}`}
              >
                <span
                  data-testid={testString(index)}
                  className="option-content"
                  title={enableTooltip ? option.text : undefined}
                >
                  {icon ? (
                    <span className={iconContainerClasses}>
                      <span className={iconClasses}>{icon}</span>
                      <span>{option.text}</span>
                    </span>
                  ) : (
                    option.text
                  )}
                </span>
                {option.count !== undefined && (
                  <span className="option-count">{option.count}</span>
                )}
              </li>
              {dividerIndices?.includes(index) && (
                <li
                  key={`${option.value + index}-divider`}
                  className="divider"
                />
              )}
            </React.Fragment>
          );
        })}
        {!options.length ? (
          <li key="no-results" className="option">
            {t(noResultsKey)}
          </li>
        ) : null}
      </ul>
    </>
  );
};
type OnChange = (value: OptionType["value"]) => void;

export interface DropdownListProps {
  name: string;
  onOpen?: () => void;
  onChange: OnChange;
  options: Array<OptionType>;
  inputReplacement?: ReactNode;
  enableTooltip?: boolean;
  value?: OptionType["value"];
  busy?: boolean;
  className?: string;
  clearable?: boolean;
  noResults?: LocalKeys;
  search?: boolean;
  fitted?: boolean;
  customValue?: boolean;
  selectOnInput?: boolean;
  tabIndex?: number;
  placeholder?: string;
  inline?: boolean;
  disabled?: boolean;
  dividerIndices?: Array<number>;
}

export const DropDownGlobals = {
  inputDebounce: process.env.NODE_ENV !== "test" ? 500 : 0
};
export const DropdownList: React.FC<DropdownListProps & {
  testid?: string;
}> = ({
  name,
  onChange,
  onOpen,
  value,
  options = [],
  inputReplacement,
  enableTooltip = false,
  className = "",
  search = false,
  busy = false,
  clearable = false,
  customValue = false,
  noResults = "NO_RESULTS",
  fitted = false,
  selectOnInput = false,
  tabIndex = 0,
  placeholder,
  inline = false,
  disabled = false,
  testid,
  dividerIndices
}) => {
  const [open, setOpen] = useState(false);
  const selected = value || false;
  const [inputValue, setInput] = useState("");
  const [focusIndex, setFocus] = useState(-1);
  const self = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const resetFocus = () => setFocus(-1);
  const opt = options.filter(option => value && option.value === value);
  let display = value ?? "";
  if (opt.length === 1) {
    display = opt[0].text;
  }
  const toggleOpen = () => {
    if (!open) {
      setInput("");
    }
    setOpen(!open);
    if (!open && onOpen) {
      onOpen();
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!self?.current?.contains(e.target as Node)) {
        setOpen(false);
        resetFocus();
      }
    };

    document.addEventListener("mousedown", handleClick, false);

    return () => {
      document.removeEventListener("mousedown", handleClick, false);
    };
  }, []);

  const debounceOnChange = useMemo(
    () =>
      debounce((value: OptionType["value"]) => {
        onChange(value);
      }, DropDownGlobals.inputDebounce),
    [onChange]
  );

  const onInputChange = (input: string) => {
    if (!open) {
      setOpen(true);
    }
    if (selectOnInput) {
      debounceOnChange(input);
    }
    setInput(input);
  };

  const onSelect = (option: OptionType) => {
    if (!option.disabled) {
      onChange(option.value);
      setOpen(false);
      setInput("");
      resetFocus();
    }
  };

  const iconClick = () => {
    if (selected && clearable) {
      setInput("");
      onChange("");
    } else {
      toggleOpen();
      resetFocus();
    }
  };

  let filtered = options;
  let icon = (
    <PolyIcon icon="keyboard_arrow_down" className={open ? "open" : ""} />
  );

  if (search && inputValue) {
    filtered = options.filter(option =>
      option.text.toLowerCase().includes(inputValue.toLowerCase())
    );
  }

  if (selected && clearable) {
    icon = <PolyIcon icon="clear_x" />;
  } else if (busy) {
    icon = <PolyIcon icon="refresh" className="spin" />;
  }

  if (customValue && search && inputValue) {
    filtered.unshift({
      text: t("CUSTOM_VALUE", { value: inputValue }),
      value: inputValue
    });
  }

  const handleKeyPress = (event: SyntheticEvent) => {
    const key = (event as KeyboardEvent).key;
    if (key === "Enter") {
      event.preventDefault();

      if (focusIndex >= 0) {
        onSelect(filtered[focusIndex]);
        resetFocus();
      } else if (inputValue?.length && search && customValue) {
        onSelect({
          text: inputValue,
          value: inputValue
        });
      }
    } else if (key === "Tab" && search) {
      setInput("");
      setOpen(false);
      resetFocus();
    } else if (key === "ArrowDown") {
      setFocus(focusIndex < filtered.length - 1 ? focusIndex + 1 : focusIndex);
    } else if (key === "ArrowUp") {
      setFocus(focusIndex >= 0 ? focusIndex - 1 : focusIndex);
    }
  };

  const classes = [
    "dropdown-list",
    className,
    fitted ? "fitted" : "",
    open ? "dropdown-open" : ""
  ];

  return (
    <div
      className={classes.filter(c => !!c).join(" ")}
      ref={self}
      data-testid={`${name}`}
    >
      <div
        className={`input-wrapper ${open ? "open" : ""}`}
        data-testid={`${name}-dropdown-list`}
      >
        <span
          className="toggle-area"
          data-testid={`${name}-dropdown-input`}
          onClick={!disabled ? toggleOpen : () => {}}
        >
          {inputReplacement ? (
            inputReplacement
          ) : (
            <input
              className={disabled ? "disabled" : open ? "open" : ""}
              type="text"
              data-testid={`${name}-input-element`}
              onChange={e => onInputChange(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={placeholder}
              value={open ? inputValue : display}
              readOnly={!search}
              tabIndex={tabIndex}
              disabled={disabled}
            />
          )}
        </span>
        {!disabled && (
          <span className="icon" onClick={iconClick} data-testid="action-icon">
            {icon}
          </span>
        )}
      </div>
      {!disabled && (
        <OptionList
          name={name}
          options={filtered}
          enableTooltip={enableTooltip}
          visible={open}
          onClick={onSelect}
          value={value}
          noResultsKey={noResults}
          onChange={onChange}
          focusIndex={focusIndex}
          inline={inline}
          disabled={disabled}
          testid={testid}
          dividerIndices={dividerIndices}
        />
      )}
    </div>
  );
};
export const DropdownListGroup: React.FC = ({ children }) => {
  return <div className="dropdown-group">{children}</div>;
};
export type DropdownLabelProps = {
  label: string;
};
export const DropDownLabel: React.FC<Pick<DropdownListProps, "value" | "name"> &
  DropdownLabelProps> = ({ value, name, label }) => {
  const selected = value || false;
  return (
    <label
      className={`type-label ${selected ? "selected" : ""}`}
      id={name + "-select-label"}
      htmlFor={name + "-select"}
    >
      {label}
    </label>
  );
};
type ListWithLabelProps = DropdownLabelProps & DropdownListProps;
const FloatingLabelDropDownList: React.FC<ListWithLabelProps> = ({
  label,
  name,
  value,
  ...rest
}) => {
  return (
    <DropdownListGroup>
      <DropDownLabel value={value} name={name} label={label} />
      <DropdownList name={name} value={value} {...rest} placeholder={label} />
    </DropdownListGroup>
  );
};
export default FloatingLabelDropDownList;
